<script lang="ts" setup>
import { computed, ref } from 'vue'
import { Icon } from '@brighterly/lib-lumee-ui'

import { ConnectionTier } from '../../model/types/classroom/enums'
import { useTracksLiveKit } from '../../model/tracks-live-kit.hook'
import { useMediaBindingLiveKit } from '../../model/media-binding-live-kit.hook'
import { useRoomLiveKit } from '../../model/room-live-kit.hook'
import { useConnectionTierLiveKit } from '../../model/connection-tier-live-kit.hook'
import ConnectionSignalGlyph from '../ConnectionSignalGlyph.vue'

defineOptions({
  name: 'VideoTileRemote',
})

const {
  remoteCameraTrack,
  isRemoteCameraMuted,
  isRemoteMicMuted,
  isRemoteSpeaking,
} = useTracksLiveKit()

const { remoteName } = useRoomLiveKit()

const { refPeerTier } = useConnectionTierLiveKit()

const videoEl = ref<HTMLVideoElement | null>(null)

useMediaBindingLiveKit(videoEl, remoteCameraTrack)

const showVideo = computed<boolean>(() => !!remoteCameraTrack.value && !isRemoteCameraMuted.value)
const initial = computed<string>(() => (remoteName.value ?? '').trim().toUpperCase()[0] || '?')
const showReconnecting = computed<boolean>(() => refPeerTier.value === ConnectionTier.Lost)
</script>

<template>
  <div class="video-tile">
    <video
      v-show="showVideo"
      ref="videoEl"
      class="video-tile__video"
      autoplay
      playsinline
    />
    <div
      v-if="!showVideo"
      class="video-tile__placeholder"
    >
      <div class="video-tile__avatar btly-text-title-xl">
        {{ initial }}
      </div>
    </div>
    <span
      v-if="showReconnecting"
      class="video-tile__reconnecting"
    >
      <ConnectionSignalGlyph
        :tier="ConnectionTier.Lost"
        surface="dark"
      />
      <span class="video-tile__reconnecting-label btly-text-title-s">Reconnecting…</span>
    </span>
    <div
      v-if="remoteName"
      class="video-tile__info btly-text-body-m"
    >
      <span
        v-if="isRemoteMicMuted"
        class="video-tile__mic"
      >
        <Icon
          name="microphone-slash"
          size="md"
          color="inherit"
        />
      </span>
      <span
        v-else
        :class="['video-tile__speaking', { 'video-tile__speaking--active': isRemoteSpeaking }]"
      >
        <span class="video-tile__speaking-bar" />
        <span class="video-tile__speaking-bar" />
        <span class="video-tile__speaking-bar" />
      </span>
      <span class="video-tile__name">{{ remoteName }}</span>
    </div>
    <div
      v-if="$slots.action"
      class="video-tile__action"
    >
      <slot name="action" />
    </div>
  </div>
</template>

<style scoped lang="scss" src="./VideoTile.scss" />
